import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Range } from "../ui/Form/Range";
import { Input } from "../ui/Form/Input";
import "./BudgetSlider.css";

interface BudgetSliderProps {
  id: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
}

// Function to snap value to the correct step
const snapToStep = (value: number, min: number, max: number): number => {
  if (value <= min) return min;
  if (value >= max) return max;
  
  // 100 steps until 1500
  if (value <= 1500) {
    return Math.round(value / 100) * 100;
  }
  // 250 steps from 1500 to 5000
  else if (value <= 5000) {
    return Math.round((value - 1500) / 250) * 250 + 1500;
  }
  // 500 steps from 5000 onwards
  else {
    return Math.round((value - 5000) / 500) * 500 + 5000;
  }
};

// Generate all valid step values
const generateStepValues = (min: number, max: number): number[] => {
  const values: number[] = [];
  
  // 100 steps until 1500
  for (let v = min; v <= 1500; v += 100) {
    values.push(v);
  }
  
  // 250 steps from 1500 to 5000
  for (let v = 1750; v <= 5000; v += 250) {
    values.push(v);
  }
  
  // 500 steps from 5000 onwards
  for (let v = 5500; v <= max; v += 500) {
    values.push(v);
  }
  
  return values;
};

export const BudgetSlider: React.FC<BudgetSliderProps> = ({
  id,
  value,
  onChange,
  min = 500,
  max = 10000,
}) => {
  const { t } = useTranslation();
  const [textValue, setTextValue] = useState(value.toString());
  
  const minText = t("step4.budget.min");
  const maxText = t("step4.budget.max");
  
  const stepValues = useMemo(() => generateStepValues(min, max), [min, max]);
  
  // Snap value to nearest valid step
  const snappedValue = useMemo(() => {
    if (value <= min) return min;
    if (value >= max) return max;
    
    // Find closest step value
    let closest = stepValues[0];
    let minDiff = Math.abs(value - closest);
    
    for (const stepValue of stepValues) {
      const diff = Math.abs(value - stepValue);
      if (diff < minDiff) {
        minDiff = diff;
        closest = stepValue;
      }
    }
    
    return closest;
  }, [value, stepValues, min, max]);

  useEffect(() => {
    // Update text value when value changes
    if (value === min) {
      setTextValue(minText);
    } else if (value === max) {
      setTextValue(maxText);
    } else {
      setTextValue(value.toString());
    }
  }, [value, min, max, minText, maxText]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = parseInt(e.target.value, 10);
    const snapped = snapToStep(rawValue, min, max);
    onChange(snapped);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setTextValue(inputValue);
    
    // Allow typing the min/max text values
    if (inputValue === minText || inputValue === maxText) {
      return;
    }
    
    const numValue = parseInt(inputValue, 10);
    if (!isNaN(numValue)) {
      const snapped = snapToStep(numValue, min, max);
      onChange(snapped);
    }
  };

  const handleTextBlur = () => {
    if (textValue === minText) {
      onChange(min);
      return;
    }
    if (textValue === maxText) {
      onChange(max);
      return;
    }
    
    const numValue = parseInt(textValue, 10);
    if (isNaN(numValue) || numValue < min) {
      onChange(min);
    } else if (numValue > max) {
      onChange(max);
    } else {
      const snapped = snapToStep(numValue, min, max);
      onChange(snapped);
    }
  };

  // Use the smallest step for the range input to allow smooth sliding
  const rangeStep = 50;

  return (
    <div className="budget-slider-container">
      <div className="budget-slider-wrapper">
        <Range
          id={id}
          min={min}
          max={max}
          step={rangeStep}
          value={snappedValue}
          onChange={handleSliderChange}
          color="primary"
          className="budget-slider"
          aria-label="Budget amount"
        />
        <div className="budget-text-wrapper">
          {value > min && value < max && <span className="budget-currency">€</span>}
          <Input
            id={`${id}-text`}
            type="text"
            value={textValue}
            onChange={handleTextChange}
            onBlur={handleTextBlur}
            unstyled
            className="budget-text-input"
            aria-label="Budget amount in euros"
          />
        </div>
      </div>
    </div>
  );
};

